import React, { useEffect, useRef, useState } from 'react'
import {
  Menu as MenuIcon,
  ExpandMore,
  ExpandLess,
  Logout,
  MailOutline,
  PeopleOutline,
  Campaign,
  Description,
  EmojiEvents,
  Person,
} from '@mui/icons-material'
import { useNavigate, Link } from 'react-router-dom'
import UserApi from '../../api/user/user'

interface SubTitle {
  id: number
  text: string
  url: string
}

interface MenuItem {
  id: number
  title: string
  icon: JSX.Element
  url?: string
  subTitles?: SubTitle[]
}

interface SidebarProps {
  menus: MenuItem[]
}

export const Sidebar: React.FC<SidebarProps> = ({ menus }) => {
  const [collapsed, setCollapsed]       = useState(true)
  const [openMenus, setOpenMenus]       = useState<Record<number, boolean>>({})
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [selectedId, setSelectedId]     = useState<number | null>(null)
  const [userName, setUserName]         = useState('')

  const nav = useNavigate()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await UserApi.listUserData()
        if (r?.Name) setUserName(r.Name)
      } catch (e) {
        console.error(e)
      }
    })()
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setCollapsed(true)
        setOpenMenus({})
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const initials = (name: string) => {
    if (!name) return ''
    const parts = name.trim().split(' ')
    return parts.length === 1
      ? parts[0].substring(0, 2).toUpperCase()
      : (parts[0][0] + parts[1][0]).toUpperCase()
  }

  const toggleCollapse  = () => setCollapsed(v => !v)
  const toggleUserMenu  = () => setUserMenuOpen(v => !v)
  const toggleDropdown  = (id: number) =>
    setOpenMenus(prev => ({ ...prev, [id]: !prev[id] }))
  const selectSimple    = (id: number, url?: string) => {
    setSelectedId(id)
    if (url) nav(url)
  }
  const logout          = () => {
    localStorage.clear()
    nav('/login')
  }

  return (
    <nav
      ref={ref}
      className={`
        flex flex-col min-h-screen bg-primary font-main
        shadow-[0px_3px_0px_5px_#999999]
        ${collapsed ? 'w-[56px]' : 'w-[219px]'}
      `}
    >
      <div className={`h-12 flex items-center ${collapsed ? 'justify-center' : 'justify-start px-4'}`}>
        <button onClick={toggleCollapse} className="p-1 bg-transparent focus:outline-none">
          <MenuIcon className="text-white" fontSize="medium" />
        </button>
      </div>
      <div className="relative">
        <button
          onClick={toggleUserMenu}
          className={`h-12 w-full flex items-center ${collapsed ? 'justify-center' : 'justify-start px-4'} bg-transparent focus:outline-none`}
        >
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
            <span className="text-primary font-bold">{initials(userName) || 'U'}</span>
          </div>
          {!collapsed && (
            <>
              <span className="ml-2 text-white font-medium truncate max-w-[90px]">
                {userName}
              </span>
              <ExpandMore className="ml-auto text-white" fontSize="small" />
            </>
          )}
        </button>
        {!collapsed && userMenuOpen && (
          <button
            onClick={logout}
            className="absolute top-12 left-0 w-full flex items-center px-4 py-2 bg-primary text-lunarRock text-sm hover:bg-white hover:text-primary focus:outline-none"
          >
            <Logout className="mr-2 text-lunarRock hover:text-primary" fontSize="small" />
            Sair
          </button>
        )}
      </div>
      <ul className="mt-2">
        {menus.map(menu => {
          const open   = !!openMenus[menu.id]
          const active = selectedId === menu.id
          const hasSub = !!menu.subTitles?.length

          return (
            <li key={menu.id}>
              <button
                onClick={() =>
                  hasSub
                    ? toggleDropdown(menu.id)
                    : selectSimple(menu.id, menu.url)
                }
                className={`
                  flex items-center h-12 w-full
                  ${collapsed ? 'justify-center' : 'justify-start px-4'}
                  text-lunarRock text-[14px] font-medium bg-transparent
                  ${active ? 'bg-primary/30 border-l-4 border-white' : 'border-l-4 border-transparent'}
                  hover:bg-primary/20 focus:outline-none
                `}
              >
                <div className="w-8 h-8 flex items-center justify-center text-lunarRock">
                  {menu.icon}
                </div>
                {!collapsed && (
                  <>
                    <span className="ml-2 flex-1 text-left">{menu.title}</span>
                    {hasSub && (
                      open
                        ? <ExpandLess className="text-lunarRock" fontSize="small" />
                        : <ExpandMore className="text-lunarRock" fontSize="small" />
                    )}
                  </>
                )}
              </button>
              {!collapsed && hasSub && open && (
                <div className="bg-primary">
                  {menu.subTitles!.map((sub, idx) => (
                    <Link
                      key={idx}
                      to={sub.url}
                      onClick={() => selectSimple(menu.id)}
                      className="flex items-center h-12 w-full pl-12 bg-primary text-lunarRock text-xs hover:bg-primary/20 border-l-4 border-transparent"
                    >
                      {sub.text}
                    </Link>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
